import { computed, toValue, type MaybeRefOrGetter } from "vue";
import { commandScore } from "../../lib/command-score";

export type KeysWithType<TOption, TType> = {
	[K in keyof TOption]: TOption[K] extends TType ? K : never;
}[keyof TOption];

type Getter<TOption, TType> = (option: TOption) => TType;

export type KeyOrGetter<TOption, TType> =
	TOption extends Record<string, any>
		? Getter<TOption, TType> | KeysWithType<TOption, TType>
		: Getter<TOption, TType>;

export type ResolveGetter<
	TOption,
	TGetter extends KeyOrGetter<TOption, any>,
> = TGetter extends keyof TOption
	? TOption[TGetter]
	: TGetter extends (opt: TOption) => infer TReturn
		? TReturn
		: never;

export type ResolveValue<
	TOption,
	TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
> = TOption extends string | number
	? TOption
	: TOption[TValueKey] extends string | number
		? TOption[TValueKey]
		: never;

export type ListOptionAcceptableValue = string | number;

export type ListProps<
	TOption,
	TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
> = {
	options: TOption[];

	optionLabel?: KeyOrGetter<TOption, string>;
	optionValue?: TValueKey;
	optionGroup?: KeyOrGetter<TOption, string>;
	optionDisabled?: KeyOrGetter<TOption, boolean>;
	optionKeywords?: KeyOrGetter<TOption, string[]>;

	sort?: boolean;
};

export type ResolvedGroup<TOption> = {
	label: string;
	options: TOption[];
};

export type OptionLabel = {
	type: "label";
	label: string;
};

export type OptionSeparator = {
	type: "separator";
};

export type OptionItem<TOption, TValue> = {
	type: "item";
	option: TOption;
	label: string;
	value: TValue;
	disabled: boolean;
};

export type OptionCustomItem<TValue> = {
	type: "custom-item";
	value: TValue;
};

export type ListOption<TOption, TValue> =
	| OptionLabel
	| OptionItem<TOption, TValue>
	| OptionSeparator
	| OptionCustomItem<TValue>;

export function useListOptions<
	TOption,
	TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
	TValue extends ResolveValue<TOption, TValueKey>,
>(props: MaybeRefOrGetter<ListProps<TOption, TValueKey>>, filter: MaybeRefOrGetter<string>) {
	function resolveGetter<TOption, TGetter extends KeyOrGetter<TOption, any>>(
		option: TOption,
		getter: TGetter
	): ResolveGetter<TOption, TGetter> {
		if (typeof getter === "function") {
			return (getter as any)(option);
		}

		if (typeof option !== "object") {
			return option as any;
		}

		return (option as any)[getter];
	}

	const groups = computed<ResolvedGroup<TOption>[]>(() => {
		const map = new Map<string, TOption[]>();

		const resolvedProps = toValue(props);

		const groupGetter = resolvedProps.optionGroup || ((() => "") as any);

		for (const option of resolvedProps.options) {
			const key = resolveGetter(option, groupGetter) as string;

			let collection = map.get(key);
			if (collection == null) {
				collection = [];
				map.set(key, collection);
			}

			collection.push(option);
		}

		return Array.from(map.entries()).map(([label, options]) => ({ label, options }));
	});

	function flattenGroups(groups: ResolvedGroup<TOption>[]) {
		const flattened: ListOption<TOption, TValue>[] = [];

		const resolvedProps = toValue(props);

		const { optionValue, optionLabel, optionDisabled } = resolvedProps;

		for (const group of groups) {
			if (group.label !== "") {
				flattened.push({ type: "label", label: group.label });
			}

			for (const option of group.options) {
				flattened.push({
					type: "item",
					option,
					label: resolveGetter(option, optionLabel || ("text" as any)) as string,
					value: resolveGetter(option, optionValue || ("value" as any)) as TValue,
					disabled: resolveGetter(option, optionDisabled || ((() => false) as any)) as boolean,
				});
			}
		}

		return flattened;
	}

	type ProcessedGroup<TOption> = {
		maxScore: number;
		label: string;
		options: TOption[];
	};

	type ProcessedOption<TOption> = {
		score: number;
		label: string;
		option: TOption;
	};

	const filteredGroups = computed<ResolvedGroup<TOption>[]>(() => {
		const propsValue = toValue(props);
		const filterValue = toValue(filter);

		const processedGroups: ProcessedGroup<TOption>[] = [];
		for (const group of groups.value) {
			const processedOptions: ProcessedOption<TOption>[] = [];
			let maxScore = 0;

			for (const option of group.options) {
				const label = resolveGetter(option, propsValue.optionLabel || ("text" as any)) as string;
				const keywords = resolveGetter(
					option,
					propsValue.optionKeywords || ((() => []) as any)
				) as string[];
				const score = commandScore(label, filterValue, keywords);

				if (score === 0) {
					continue;
				}

				processedOptions.push({ option, label, score });
				maxScore = Math.max(maxScore, score);
			}

			// Skip this group if no options are found.
			if (processedOptions.length === 0) {
				continue;
			}

			if (propsValue.sort) {
				processedOptions.sort((a, b) => {
					if (a.score === b.score) {
						// Sort by label, ascending.
						return a.label.localeCompare(b.label);
					}

					// Sort by score, descending.
					return b.score - a.score;
				});
			}

			processedGroups.push({
				maxScore,
				label: group.label,
				options: processedOptions.map((o) => o.option),
			});
		}

		if (propsValue.sort) {
			processedGroups.sort((a, b) => {
				if (a.maxScore === b.maxScore) {
					return a.label.localeCompare(b.label);
				}

				return b.maxScore - a.maxScore;
			});
		}

		return processedGroups.map(({ label, options }) => ({ label, options }));
	});

	const options = computed(() => flattenGroups(groups.value));
	const filteredOptions = computed(() => flattenGroups(filteredGroups.value));

	return {
		options,
		filteredOptions,
	};
}
